import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { REGISTRATION_CONSENT_TYPES, type ConsentCheckbox } from './consent-documents';

// ─── member-facing registration consents API ────────────────────────────────
//
// `POST /api/v1/members/me/consents`: a signed-in member agrees to the two
// registration consent controls outside sign-up (the migration re-consent).
// The request is strict, because a consent write must never accept a shape it
// did not mean to; the response is client-parsed, so it strips unknown keys.

const REGISTRATION_CONTROLS = ['terms', 'care'] as const satisfies readonly ConsentCheckbox[];

export const registrationConsentManifestSchema = z.object({
  terms_of_use: z.string().min(1),
  privacy_policy: z.string().min(1),
  notice_of_privacy_practices: z.string().min(1),
  telehealth_informed: z.string().min(1),
  part2_release: z.string().min(1),
  tcpa_authorization: z.string().min(1),
});
export type RegistrationConsentManifest = z.infer<typeof registrationConsentManifestSchema>;
export const registrationConsentLinksSchema = z.object({
  terms_of_use: z.string().url(),
  privacy_policy: z.string().url(),
  notice_of_privacy_practices: z.string().url(),
  telehealth_informed: z.string().url(),
  part2_release: z.string().min(1),
  tcpa_authorization: z.string().url(),
});
export const registrationConsentDecisionsSchema = z.object({
  terms_of_use: z.number().int().nonnegative(),
  privacy_policy: z.number().int().nonnegative(),
  notice_of_privacy_practices: z.number().int().nonnegative(),
  telehealth_informed: z.number().int().nonnegative(),
  part2_release: z.number().int().nonnegative(),
  tcpa_authorization: z.number().int().nonnegative(),
  calls_and_texts: z.number().int().nonnegative(),
});
export type RegistrationConsentDecisions = z.infer<typeof registrationConsentDecisionsSchema>;

// ─── what a capture may say it displayed ────────────────────────────────────
//
// A queued capture outlives the registry: a build whose standing read predates
// the TCPA Authorization persisted five document versions and five decision
// counters, and the app update that brings the sixth document must still
// decode that entry and send it, so the server can answer
// `stale_consent_document` and the member re-consents on current metadata.
// The capture shapes therefore accept the earlier five-document manifest.
// They still accept no document the registry does not serve, and only the
// server decides whether a displayed manifest is current: a manifest missing
// a served document is stale, never a grant of the document it lacks.
export const registrationConsentCaptureManifestSchema = registrationConsentManifestSchema.partial({
  tcpa_authorization: true,
});
export type RegistrationConsentCaptureManifest = z.infer<
  typeof registrationConsentCaptureManifestSchema
>;
export const registrationConsentCaptureDecisionsSchema = registrationConsentDecisionsSchema.partial(
  { tcpa_authorization: true },
);
export type RegistrationConsentCaptureDecisions = z.infer<
  typeof registrationConsentCaptureDecisionsSchema
>;

export const registrationConsentStandingSchema = z.object({
  migrated: z.boolean(),
  importPending: z.boolean(),
  decisions: registrationConsentDecisionsSchema,
  required: z.boolean(),
  consentPending: z.boolean(),
  resumeJourney: z.boolean(),
  versions: registrationConsentManifestSchema,
  links: registrationConsentLinksSchema,
});
export type RegistrationConsentStanding = z.infer<typeof registrationConsentStandingSchema>;
export const registrationConsentStandingResponseSchema = apiEnvelope(
  registrationConsentStandingSchema,
);

export const registrationConsentsRequestSchema = z
  .object({
    // Both controls, in any order, each once: agreeing to one control alone
    // is not a registration consent.
    controls: z
      .array(z.enum(REGISTRATION_CONTROLS))
      .length(REGISTRATION_CONTROLS.length)
      .refine((controls) => new Set(controls).size === controls.length, {
        message: 'each control may appear once',
      }),
    flow: z.literal('migration'),
    acceptance_id: z.string().min(1).max(64).optional(),
    displayed_decisions: registrationConsentCaptureDecisionsSchema.strict().optional(),
    accepted_at: z.string().datetime({ offset: true }).optional(),
    displayed_versions: registrationConsentCaptureManifestSchema.strict().optional(),
    calls_and_texts_consent: z.boolean().optional(),
    surface: z.enum(['mobile', 'web']).default('mobile'),
    locale: z.string().min(1).max(35).optional(),
    app_version: z.string().min(1).max(64).optional(),
  })
  .strict()
  .refine((r) => r.calls_and_texts_consent !== true || r.displayed_versions !== undefined, {
    message: 'displayed versions required for clause acceptance',
  })
  .refine((r) => (r.acceptance_id === undefined) === (r.displayed_decisions === undefined), {
    message: 'capture identity and decision versions must be supplied together',
  });
export type RegistrationConsentsRequest = z.infer<typeof registrationConsentsRequestSchema>;

export const registrationConsentResultSchema = z.object({
  consentType: z.enum(REGISTRATION_CONSENT_TYPES),
  documentVersion: z.string(),
  recordedAt: z.string().datetime({ offset: true }),
  /** False when this capture or a qualifying grant was already recorded. */
  created: z.boolean(),
});
export type RegistrationConsentResult = z.infer<typeof registrationConsentResultSchema>;

export const registrationConsentsDataSchema = z.object({
  consents: z.array(registrationConsentResultSchema),
});
export type RegistrationConsentsData = z.infer<typeof registrationConsentsDataSchema>;

export const registrationConsentsResponseSchema = apiEnvelope(registrationConsentsDataSchema);
export type RegistrationConsentsResponse = z.infer<typeof registrationConsentsResponseSchema>;
